import { EnergyData } from "../types";
import DataChart from "./DataChart";
import { getSolarSum, getBessSum, formatEnergy, getConsumption, getConsumptionSum } from "../utils";
import { ChevronDoubleDownIcon, ChevronDoubleUpIcon } from "@heroicons/react/16/solid";
import { InstanceWithGenerationSummary } from "../../../types";
import * as React from "react";

const DataSummary: React.FC<{
    instanceWithGenerationSummary: InstanceWithGenerationSummary;
    energyData: EnergyData | null;
    loading: boolean;
    summaryLoading?: boolean;
}> = ({ instanceWithGenerationSummary, energyData, loading, summaryLoading }) => {
    return <>
        {/* Left-aligned stats */}
        <div className="flex flex-col gap-2 items-start">
            <div className="flex flex-row items-center">
                <span className="text-white w-30">Solar PV</span>
                {
                    loading
                        ? <span className="loading loading-dots text-solar"></span>
                        : energyData
                            ? <span className="text-solar font-semibold">{getSolarSum(energyData)} kWh</span>
                            : <span className="text-solar font-semibold">- kWh</span>
                }
            </div>
            <div className="flex flex-row items-center">
                <span className="text-white w-30">BESS</span>
                <ChevronDoubleUpIcon className="h-4 w-4 text-bess-charging" />
                {
                    loading
                        ? <span className="loading loading-dots text-bess-charging"></span>
                        : energyData
                            ? <span className="text-bess-charging font-semibold">{getBessSum(energyData, 'charging')} kWh</span>
                            : <span className="text-bess-charging font-semibold">- kWh</span>
                }
                <ChevronDoubleDownIcon className="h-4 w-4 text-bess-discharging ms-1" />
                {
                    loading
                        ? <span className="loading loading-dots text-bess-discharging"></span>
                        : energyData
                            ? <span className="text-bess-discharging font-semibold">{getBessSum(energyData, 'discharging')} kWh</span>
                            : <span className="text-bess-discharging font-semibold">- kWh</span>
                }
            </div>
            <div className="flex flex-row items-center">
                <span className="text-white w-30">Consumption</span>
                {
                    loading
                        ? <span className="loading loading-dots text-pacificgreen-300"></span>
                        : energyData
                            ? <span className="text-pacificgreen-300 font-semibold">{
                                formatEnergy(getConsumptionSum(energyData))}</span>
                            : <span className="text-pacificgreen-300 font-semibold">- kWh</span>
                }
            </div>
        </div>
        {/* Data chart */}
        <div className="h-60 w-full">
            <DataChart energyData={energyData} loading={loading} />
        </div>
        <div className="flex flex-row items-center gap-4">
            <p className="text-white font-semibold">Total</p>
            <div className="flex flex-row items-center">
                <span className="text-white mr-2">Solar PV</span>
                {
                    summaryLoading
                        ? <span className="loading loading-dots text-solar"></span>
                        : instanceWithGenerationSummary.results?.["EnergyAndPowerPv"]?.["pvGeneration"]
                            ? <span className="text-solar font-semibold">{formatEnergy(instanceWithGenerationSummary.results["EnergyAndPowerPv"]?.["pvGeneration"])}</span>
                            : <span className="text-solar font-semibold">- kWh</span>
                }
            </div>
            <div className="flex flex-row items-center">
                <span className="text-white mr-2">BESS</span>
                <ChevronDoubleUpIcon className="h-4 w-4 text-bess-charging" />
                {
                    summaryLoading
                        ? <span className="loading loading-dots text-bess-charging"></span>
                        : instanceWithGenerationSummary.results?.["EnergyAndPowerBattery"]?.["batteryCharging"]
                            ? <span className="text-bess-charging font-semibold">{formatEnergy(instanceWithGenerationSummary.results["EnergyAndPowerBattery"]?.["batteryCharging"])}</span>
                            : <span className="text-bess-charging font-semibold">- kWh</span>
                }
                <ChevronDoubleDownIcon className="h-4 w-4 text-bess-discharging ms-1" />
                {
                    summaryLoading
                        ? <span className="loading loading-dots text-bess-discharging"></span>
                        : instanceWithGenerationSummary.results?.["EnergyAndPowerBattery"]?.["batteryDischarging"]
                            ? <span className="text-bess-discharging font-semibold">{formatEnergy(instanceWithGenerationSummary.results["EnergyAndPowerBattery"]?.["batteryDischarging"])}</span>
                            : <span className="text-bess-discharging font-semibold">- kWh</span>
                }
            </div>
            <div className="flex flex-row items-center">
                <span className="text-white mr-2">Consumption</span>
                {
                    summaryLoading
                        ? <span className="loading loading-dots text-pacificgreen-300"></span>
                        : instanceWithGenerationSummary.results?.["EnergyAndPowerBattery"]?.["batteryDischarging"]
                            && instanceWithGenerationSummary.results?.["EnergyAndPowerBattery"]?.["batteryCharging"]
                            && instanceWithGenerationSummary.results?.["EnergyAndPowerPv"]?.["pvGeneration"]
                            ? <span className="text-pacificgreen-300 font-semibold">{
                                formatEnergy(getConsumption(instanceWithGenerationSummary))}</span>
                            : <span className="text-pacificgreen-300 font-semibold">- kWh</span>
                }
            </div>
        </div>
    </>
};

export default DataSummary;
